import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Input, Button, Card, CardBody } from '@heroui/react';
import { Eye, EyeOff, Mail, Lock, User, Check, X } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

type Mode = 'login' | 'signup';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const verified = searchParams.get('verified');

  const { login, signup } = useAuthStore();

  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    verified === 'false' ? 'Verification link is invalid or expired.' : null,
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(
    verified === 'true' ? 'Email verified! You can now sign in.' : null,
  );

  const toggleMode = () => {
    setMode((prev) => (prev === 'login' ? 'signup' : 'login'));
    setError(null);
    setSuccessMessage(null);
    setName('');
    setEmail('');
    setPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        navigate(from, { replace: true });
      } else {
        await signup(email, password, name);
        setSuccessMessage('Check your email for a verification link.');
        setMode('login');
        setName('');
        setPassword('');
      }
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };
      const msg =
        axiosError?.response?.data?.message ??
        axiosError?.response?.data?.error ??
        axiosError?.message ??
        'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <div className="w-full max-w-sm">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold tracking-tight font-ui"
            style={{ color: 'var(--accent)' }}
          >
            Anvil
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        {/* Card */}
        <Card
          classNames={{
            base: 'border border-[var(--border)]',
          }}
          style={{ backgroundColor: 'var(--bg-surface)' }}
        >
          <CardBody className="p-6 gap-0">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Name field — signup only */}
              {mode === 'signup' && (
                <Input
                  label="Full Name"
                  placeholder="John Smith"
                  value={name}
                  onValueChange={setName}
                  isRequired
                  autoComplete="name"
                  startContent={
                    <User size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  }
                  classNames={{
                    inputWrapper:
                      'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
                    input: 'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    label: 'text-[var(--text-secondary)] text-sm',
                  }}
                  variant="bordered"
                />
              )}

              {/* Email */}
              <Input
                type="email"
                label="Email"
                placeholder="you@company.com"
                value={email}
                onValueChange={setEmail}
                isRequired
                autoComplete="email"
                startContent={
                  <Mail size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                }
                classNames={{
                  inputWrapper:
                    'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
                  input: 'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                  label: 'text-[var(--text-secondary)] text-sm',
                }}
                variant="bordered"
              />

              {/* Password */}
              <Input
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                value={password}
                onValueChange={setPassword}
                isRequired
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                startContent={
                  <Lock size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                }
                endContent={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff size={16} style={{ color: 'var(--text-muted)' }} />
                    ) : (
                      <Eye size={16} style={{ color: 'var(--text-muted)' }} />
                    )}
                  </button>
                }
                classNames={{
                  inputWrapper:
                    'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
                  input: 'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                  label: 'text-[var(--text-secondary)] text-sm',
                }}
                variant="bordered"
              />

              {/* Password strength — signup only */}
              {mode === 'signup' && password.length > 0 && (
                <div className="flex flex-col gap-1 -mt-2">
                  {[
                    { label: 'At least 8 characters', test: password.length >= 8 },
                    { label: 'One uppercase letter', test: /[A-Z]/.test(password) },
                    { label: 'One number', test: /[0-9]/.test(password) },
                  ].map((rule) => (
                    <div
                      key={rule.label}
                      className="flex items-center gap-1.5 text-xs"
                      style={{
                        color: rule.test
                          ? 'var(--green, #4ade80)'
                          : 'var(--text-muted)',
                      }}
                    >
                      {rule.test ? <Check size={12} /> : <X size={12} />}
                      {rule.label}
                    </div>
                  ))}
                </div>
              )}

              {/* Forgot password link — login only */}
              {mode === 'login' && (
                <div className="flex justify-end -mt-2">
                  <Link
                    to="/auth/forgot-password"
                    className="text-xs font-medium hover:underline"
                    style={{ color: 'var(--accent)' }}
                  >
                    Forgot password?
                  </Link>
                </div>
              )}

              {/* Success */}
              {successMessage && (
                <div
                  className="text-sm px-3 py-2 rounded-lg border"
                  style={{
                    color: 'var(--green, #4ade80)',
                    backgroundColor: 'rgba(74, 222, 128, 0.08)',
                    borderColor: 'rgba(74, 222, 128, 0.2)',
                  }}
                >
                  {successMessage}
                </div>
              )}

              {/* Error */}
              {error && (
                <div
                  className="text-sm px-3 py-2 rounded-lg border"
                  style={{
                    color: 'var(--red)',
                    backgroundColor: 'rgba(248, 113, 113, 0.08)',
                    borderColor: 'rgba(248, 113, 113, 0.2)',
                  }}
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                color="primary"
                isLoading={isLoading}
                className="mt-1 font-medium"
                fullWidth
              >
                {isLoading
                  ? mode === 'login'
                    ? 'Signing in…'
                    : 'Creating account…'
                  : mode === 'login'
                    ? 'Sign In'
                    : 'Create Account'}
              </Button>
            </form>

            {/* Toggle mode */}
            <div className="mt-5 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="font-medium hover:underline focus:outline-none"
                style={{ color: 'var(--accent)' }}
              >
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
